import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useParams } from 'react-router-dom';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';
import { tenantApi } from '../services/api';

const ClinicAdminPasien = () => {
    const { slug: routeSlug } = useParams();
    const [patients, setPatients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    // Modals State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', email: '', username: '', password: '' });

    const slug =
        routeSlug ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')?.klinik?.slug;
            } catch {
                return null;
            }
        })();

    useEffect(() => {
        if (!slug) return;
        fetchPatients();
    }, [slug]);

    const fetchPatients = async () => {
        if (!slug) return;
        try {
            setIsLoading(true);
            const response = await tenantApi(slug).pasien.getAll();
            if (response.data && response.data.success) {
                const mappedPatients = response.data.data.map(p => ({
                    id: p.id_pasien,
                    name: p.nama_pasien,
                    email: p.email,
                    username: p.username || '',
                    avatarColor: getRandomColor()
                }));
                setPatients(mappedPatients);
            }
        } catch (error) {
            console.error("Error fetching patients:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getRandomColor = () => {
        const colors = ['bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-purple-100', 'bg-pink-100', 'bg-cyan-100'];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    // Handlers
    const handleEditClick = (patient) => {
        setSelectedPatient(patient);
        setEditForm({ name: patient.name, email: patient.email, username: patient.username || '', password: '' });
        setIsEditModalOpen(true);
    };

    const handleCreateClick = () => {
        setSelectedPatient(null);
        setEditForm({ name: '', email: '', username: '', password: '' });
        setIsCreateModalOpen(true);
    };

    const handleDeleteClick = (patient) => {
        setSelectedPatient(patient);
        setIsDeleteModalOpen(true);
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                nama_pasien: editForm.name,
                email: editForm.email,
                username: editForm.username,
            };
            if (editForm.password) {
                payload.password = editForm.password;
            }

            await tenantApi(slug).pasien.update(selectedPatient.id, payload);
            fetchPatients();
            setIsEditModalOpen(false);
            setSelectedPatient(null);
        } catch (error) {
            console.error("Error updating patient:", error);
            alert("Gagal memperbarui data pasien");
        }
    };

    const handleCreatePatient = async (e) => {
        e.preventDefault();
        try {
            await tenantApi(slug).pasien.create({
                nama_pasien: editForm.name,
                email: editForm.email,
                username: editForm.username,
                password: editForm.password,
            });
            await fetchPatients();
            setIsCreateModalOpen(false);
            setEditForm({ name: '', email: '', username: '', password: '' });
        } catch (error) {
            console.error("Error creating patient:", error);
            const msg =
                error.response?.data?.message ||
                (error.response?.data?.errors && JSON.stringify(error.response.data.errors)) ||
                "Gagal menambahkan pasien";
            alert(msg);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await tenantApi(slug).pasien.delete(selectedPatient.id);
            fetchPatients();
            setIsDeleteModalOpen(false);
            setSelectedPatient(null);
        } catch (error) {
            console.error("Error deleting patient:", error);
            alert("Gagal menghapus data pasien");
        }
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedPatients = [...patients].sort((a, b) => {
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        return String(a?.[sortConfig.key] ?? '').localeCompare(String(b?.[sortConfig.key] ?? ''), 'id', { numeric: true }) * direction;
    });
    const totalPages = Math.max(1, Math.ceil(sortedPatients.length / perPage));
    const paginatedPatients = sortedPatients.slice((currentPage - 1) * perPage, currentPage * perPage);

    if (!slug) {
        return (
            <ClinicAdminLayout activePage="pasien" title="Pasien">
                <p className="text-sm text-gray-500">
                    Slug klinik tidak ditemukan. Buka admin dari URL <code className="bg-gray-100 px-1 rounded">/nama-klinik/clinic-admin/pasien</code>.
                </p>
            </ClinicAdminLayout>
        );
    }

    return (
        <ClinicAdminLayout activePage="pasien" title="Pasien">
            {/* Pasien Table Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-8">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 leading-none">Pasien</h3>
                    <button
                        onClick={handleCreateClick}
                        className="px-6 py-3 bg-gradient-to-r from-[#06B6D4] to-[#038DA5] text-white font-bold text-sm rounded-xl hover:opacity-90 transition-all shadow-lg shadow-cyan-500/30 flex items-center gap-2"
                    >
                        <Icon icon="material-symbols:add" className="text-lg" />
                        Tambah Pasien
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th onClick={() => handleSort('name')} className="cursor-pointer px-10 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-l-xl">Nama Pasien</th>
                                <th onClick={() => handleSort('email')} className="cursor-pointer px-10 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Email</th>
                                <th className="px-10 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider rounded-r-xl pr-16">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {paginatedPatients.length > 0 ? (
                                paginatedPatients.map((patient) => (
                                    <tr key={patient.id} className="hover:bg-gray-50 transition-colors group">
                                        <td className="px-10 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 ${patient.avatarColor} rounded-full flex-shrink-0`}></div>
                                                <span className="font-bold text-sm text-gray-700">{patient.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-10 py-5 text-sm font-medium text-gray-500 text-center">{patient.email}</td>
                                        <td className="px-10 py-5">
                                            <div className="flex items-center justify-end gap-2 pr-6">
                                                <button
                                                    onClick={() => handleEditClick(patient)}
                                                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-[#06B6D4] hover:border-[#06B6D4] transition-all"
                                                >
                                                    <Icon icon="material-symbols:edit-note-outline" className="text-xl" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(patient)}
                                                    className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-500 transition-all"
                                                >
                                                    <Icon icon="material-symbols:delete-outline" className="text-xl" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-10 py-8 text-center text-gray-500 text-sm">
                                        Tidak ada data pasien
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                    <span className="text-sm text-gray-500">{currentPage}/{totalPages}</span>
                    <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
            </div>

            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={() => setIsCreateModalOpen(false)}></div>
                    <div className="bg-white rounded-[32px] w-full max-w-sm p-8 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Tambah Pasien</h3>
                            <button onClick={() => setIsCreateModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <Icon icon="material-symbols:close" className="text-gray-400 text-xl" />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePatient} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama Pasien</label>
                                <input type="text" value={editForm.name} onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all" placeholder="Nama Lengkap" required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                <input type="email" value={editForm.email} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all" placeholder="Email Address" required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Username</label>
                                <input type="text" value={editForm.username} onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all" placeholder="Username" required />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Password</label>
                                <input type="password" value={editForm.password} onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all" placeholder="Password" required />
                            </div>
                            <button type="submit" className="w-full py-3 mt-4 bg-[#06B6D4] text-white font-bold rounded-xl shadow-lg shadow-cyan-500/20 hover:bg-[#038DA5] transition-all active:scale-[0.98]">Tambah Pasien</button>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={() => setIsEditModalOpen(false)}></div>
                    <div className="bg-white rounded-[32px] w-full max-w-sm p-8 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Edit Pasien</h3>
                            <button onClick={() => setIsEditModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <Icon icon="material-symbols:close" className="text-gray-400 text-xl" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveEdit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama Pasien</label>
                                <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all"
                                    placeholder="Nama Lengkap"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all"
                                    placeholder="Email Address"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Username</label>
                                <input
                                    type="text"
                                    value={editForm.username}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all"
                                    placeholder="Username"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Password (Kosongkan jika tidak diganti)</label>
                                <input
                                    type="password"
                                    value={editForm.password}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/20 focus:border-[#06B6D4] transition-all"
                                    placeholder="Password Baru"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 mt-4 bg-[#06B6D4] text-white font-bold rounded-xl shadow-lg shadow-cyan-500/20 hover:bg-[#038DA5] transition-all active:scale-[0.98]"
                            >
                                Simpan Perubahan
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity" onClick={() => setIsDeleteModalOpen(false)}></div>
                    <div className="bg-white rounded-[40px] w-full max-w-sm p-10 relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300 flex flex-col items-center text-center">
                        <div className="w-24 h-24 bg-[#FF6B6B] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                            <span className="text-white text-5xl font-bold leading-none -mt-1">!</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Hapus Pasien?</h3>
                        <p className="text-gray-500 text-sm font-medium mb-10 leading-relaxed px-4">
                            Apakah anda yakin ingin menghapus data pasien <strong>{selectedPatient?.name}</strong>?
                        </p>
                        <div className="w-full space-y-4">
                            <button
                                onClick={handleConfirmDelete}
                                className="w-full py-4 bg-[#FF6B6B] text-white font-bold rounded-2xl shadow-lg shadow-red-500/20 hover:bg-[#ff5252] transition-colors active:scale-95"
                            >
                                Ya, Hapus
                            </button>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="w-full py-4 bg-white text-gray-500 font-bold rounded-2xl border border-gray-100 shadow-sm hover:bg-gray-50 transition-colors active:scale-95"
                            >
                                Batal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ClinicAdminLayout>
    );
};

export default ClinicAdminPasien;
